import { WorkerStage } from "@clipboard-health/contract-worker-app-bff";
import { Alert } from "@mui/material";
import { PullToRefresh } from "@src/appV2/lib/PullToRefresh/PullToRefresh";
import { PageWrapper } from "@src/appV2/redesign/components/PageWrapper";
import { StickyScrollPageHeader } from "@src/appV2/redesign/components/StickyScrollPageHeader";
import { useIsInHomeEnabled } from "@src/appV2/redesign/InHome/lib/useIsInHomeEnabled";
import { useWorkerPendingVisitInvites } from "@src/appV2/redesign/InHome/VisitInvites/api/useWorkerPendingVisitInvites";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useRef } from "react";

import { PageContent } from "../components/PageContent";
import { useWorkerPendingShiftInvites } from "./api/useWorkerPendingShiftInvites";
import { ShiftInvitesEmptyState } from "./EmptyState";
import { ShiftInvitesList } from "./List";
import { ShiftInvitesLoadingPageState } from "./LoadingPageState";
import { MixedInvitesList } from "./MixedInvitesList";

export function ShiftInvitesPage() {
  const worker = useDefinedWorker();
  const hideBrowseShiftsButton = worker.stage === WorkerStage.RESTRICTED;

  const {
    data: shiftInvitesData,
    refetch: refetchShiftInvites,
    isLoading: isShiftInvitesLoading,
    isError: isShiftInvitesError,
    isSuccess: isShiftInvitesSuccess,
  } = useWorkerPendingShiftInvites();

  const isInHomeEnabled = useIsInHomeEnabled();

  const pendingShiftInvites = shiftInvitesData?.data ?? [];

  const {
    data: visitInvitesData,
    refetch: refetchVisitInvites,
    isSuccess: isVisitInvitesSuccess,
    isLoading: isVisitInvitesLoading,
  } = useWorkerPendingVisitInvites({
    options: {
      enabled: isInHomeEnabled,
    },
  });

  const pendingVisitInvites = isVisitInvitesSuccess ? (visitInvitesData?.data ?? []) : [];

  const isLoading = isShiftInvitesLoading || (isVisitInvitesLoading && isInHomeEnabled);
  const isSuccess = isShiftInvitesSuccess && (!isInHomeEnabled || isVisitInvitesSuccess);

  const scrollRef = useRef<HTMLDivElement>(null);

  function renderInvitesList() {
    const hasAnyInvites = pendingShiftInvites.length > 0 || pendingVisitInvites.length > 0;

    if (!hasAnyInvites) {
      return <ShiftInvitesEmptyState hideBrowseShiftsButton={hideBrowseShiftsButton} />;
    }

    if (pendingVisitInvites.length > 0) {
      return (
        <MixedInvitesList shiftInvites={pendingShiftInvites} visitInvites={pendingVisitInvites} />
      );
    }

    return <ShiftInvitesList shiftInvites={pendingShiftInvites} />;
  }

  const title = pendingVisitInvites.length > 0 ? "Invites" : "Shift invites";
  return (
    <PageWrapper>
      <StickyScrollPageHeader title={title} isLoading={isLoading} scrollContainerRef={scrollRef} />

      <PageContent ref={scrollRef}>
        <PullToRefresh
          onRefresh={async () => {
            await Promise.allSettled([refetchShiftInvites(), refetchVisitInvites()]);
          }}
        />
        {isLoading && <ShiftInvitesLoadingPageState />}
        {isShiftInvitesError && <Alert severity="error">Error loading shift invites.</Alert>}
        {isSuccess && renderInvitesList()}
      </PageContent>
    </PageWrapper>
  );
}
